import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getInventoryTrend, type ChronicStatus, type InventoryTrendPoint } from "../api/rest";
import { seriesOf, latestDelta, layout, formatDelta, pointLabel, shortDate, axisLabel } from "./inventoryTrend";
import tbl from "./FleetPage.module.css";
import css from "./InventoryTrendSection.module.css";

/**
 * ADR 362 — "is it getting better?" One card per active warehouse: three stat
 * tiles (last value + change), small multiples over the last runs, and the
 * addresses that keep diverging. Every verdict comes from the pinned ledger, so
 * a WMS re-upload cannot repaint the past.
 */
const NO_POINTS: InventoryTrendPoint[] = [];

export function InventoryTrendSection({ siteId, warehouseId }: { siteId: string; warehouseId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const query = useQuery({
    queryKey: ["inventory-trend", siteId, warehouseId],
    queryFn: () => getInventoryTrend(siteId, warehouseId),
  });
  const trend = query.data;
  const points = trend?.runs ?? NO_POINTS;
  const series = useMemo(() => seriesOf(points), [points]);

  if (query.isPending) return <p className={css.note} role="status">{t("inventory.trend.loading")}</p>;
  if (query.isError || !trend) return <p className={css.note} role="alert">{t("inventory.trend.error")}</p>;
  if (points.length < 2) {
    return (
      <section className={css.card} aria-label={t("inventory.trend.title")}>
        <h2 className={css.title}>{t("inventory.trend.title")}</h2>
        <p className={css.note}>{t("inventory.trend.needTwo")}</p>
      </section>
    );
  }

  const agreement = latestDelta(series.agreement);
  const divergent = latestDelta(series.divergent);
  const coverage = latestDelta(series.coverage);
  const addresses = latestDelta(series.addresses);
  const pct = (v: number) => `${v.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  const int = (v: number) => v.toLocaleString(locale);

  return (
    <section className={css.card} aria-label={t("inventory.trend.title")}>
      <div className={css.head}>
        <h2 className={css.title}>{t("inventory.trend.title")}</h2>
      </div>
      {!trend.wmsSnapshot && <p className={css.note}>{t("inventory.trend.noWms")}</p>}

      <div className={css.tiles}>
        {trend.wmsSnapshot && agreement && (
          <Tile label={t("inventory.trend.agreement")} value={pct(agreement.value)}
            delta={agreement.delta} unit="pp" upIsGood t={t} locale={locale} />
        )}
        {trend.wmsSnapshot && divergent && (
          <Tile label={t("inventory.trend.divergent")} value={int(divergent.value)}
            delta={divergent.delta} unit="count" upIsGood={false} t={t} locale={locale} />
        )}
        {coverage && (
          <Tile label={t("inventory.trend.coverage")} value={pct(coverage.value)}
            delta={coverage.delta} unit="pp" upIsGood t={t} locale={locale} />
        )}
        {addresses && (
          <Tile label={t("inventory.trend.addresses")} value={int(addresses.value)}
            delta={addresses.delta} unit="count" upIsGood t={t} locale={locale} />
        )}
      </div>

      <div className={css.charts}>
        {trend.wmsSnapshot && (
          <TrendChart title={t("inventory.trend.agreement")} points={points} values={series.agreement}
            percent kind="line" format={pct} locale={locale} t={t} />
        )}
        {trend.wmsSnapshot && (
          <TrendChart title={t("inventory.trend.divergent")} points={points} values={series.divergent}
            percent={false} kind="bar" format={int} locale={locale} t={t} />
        )}
        {/* ADR 410: counts are bars, percentages are lines — the meeting's rule. */}
        <TrendChart title={t("inventory.trend.coverage")} points={points} values={series.coverage}
          percent kind="line" format={pct} locale={locale} t={t} />
        <TrendChart title={t("inventory.trend.addresses")} points={points} values={series.addresses}
          percent={false} kind="bar" format={int} locale={locale} t={t} />
      </div>

      <details className={css.tableView}>
        <summary>{t("inventory.trend.tableView")}</summary>
        <div className={css.scroll}>
          <table className={tbl.table}>
            <thead><tr>
              <th>{t("inventory.trend.col.run")}</th><th>{t("inventory.trend.col.date")}</th>
              <th>{t("inventory.trend.addresses")}</th><th>{t("inventory.trend.coverage")}</th>
              {trend.wmsSnapshot && <><th>{t("inventory.trend.agreement")}</th><th>{t("inventory.trend.divergent")}</th></>}
            </tr></thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.runId}>
                  <td><Link to={`/inventory/runs/${p.runId}`}>{pointLabel(p, locale)}</Link></td>
                  <td className={tbl.mono}>{p.startedAt ? shortDate(p.startedAt, locale) : "—"}</td>
                  <td className={tbl.mono}>{p.addressesScanned === null ? "—" : int(p.addressesScanned)}</td>
                  <td className={tbl.mono}>{p.coveragePct == null ? "—" : pct(p.coveragePct)}</td>
                  {trend.wmsSnapshot && <>
                    <td className={tbl.mono}>{p.judgement ? pct(p.judgement.agreementPct) : "—"}</td>
                    <td className={tbl.mono}>{p.judgement ? int(p.judgement.divergent) : "—"}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {trend.wmsSnapshot && (
        <div className={css.chronic}>
          <h3 className={css.chartTitle}>{t("inventory.trend.chronicTitle")}</h3>
          <p className={css.chronicHint}>
            {t("inventory.trend.chronicHint", { min: trend.chronicMinHits, window: trend.chronicWindow })}
            {trend.chronic.length > 0 && ` ${t("inventory.trend.chronicCount", { count: trend.chronic.length })}`}
          </p>
          {trend.chronic.length === 0 ? (
            <p className={css.note}>{t("inventory.trend.chronicNone", { min: trend.chronicMinHits, window: trend.chronicWindow })}</p>
          ) : (
            <div className={css.chronicScroll}>
              <table className={tbl.table} aria-label={t("inventory.trend.chronicTitle")}>
                <thead><tr>
                  <th>{t("inventory.trend.col.address")}</th>
                  <th>{t("inventory.trend.col.hits")}</th>
                  <th>{t("inventory.trend.col.lastStatus")}</th>
                  <th>{t("inventory.trend.col.product")}</th>
                  <th>{t("inventory.trend.col.lastRun")}</th>
                </tr></thead>
                <tbody>
                  {trend.chronic.map((c) => (
                    <tr key={c.address}>
                      <td className={tbl.mono}>{c.address}</td>
                      <td className={tbl.mono}>{t("inventory.trend.hitsOf", { hits: c.hits, runs: c.runsConsidered })}</td>
                      <td><StatusChip status={c.lastStatus} t={t} /></td>
                      <td className={tbl.mono}>{c.lastProduct}</td>
                      <td><Link to={`/inventory/runs/${c.lastRunId}`}>{t("inventory.trend.seeRun")}</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

type T = (key: string, opts?: Record<string, unknown>) => string;

function Tile({ label, value, delta, unit, upIsGood, t, locale }: {
  label: string; value: string; delta: number | null; unit: "pp" | "count"; upIsGood: boolean; t: T; locale: string;
}) {
  const cls = delta === null || Math.abs(delta) < 0.05 ? "" : (delta > 0) === upIsGood ? css.deltaUp : css.deltaDown;
  return (
    <div className={css.tile}>
      <div className={css.tileLabel}>{label}</div>
      <div className={css.tileValue}>{value}</div>
      <div className={`${css.tileDelta} ${cls}`}>
        {delta === null
          ? t("inventory.trend.noPrevious")
          : t(unit === "pp" ? "inventory.trend.deltaPp" : "inventory.trend.deltaCount",
              { delta: formatDelta(delta, locale, unit === "pp" ? 1 : 0) })}
      </div>
    </div>
  );
}

const W = 320, H = 110, PAD = 14, PAD_L = 40;

function TrendChart({ title, points, values, percent, kind, format, locale, t }: {
  title: string; points: InventoryTrendPoint[]; values: (number | null)[]; percent: boolean;
  kind: "line" | "bar"; format: (v: number) => string; locale: string; t: T;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const g = layout(values, W, H, PAD, percent, PAD_L);
  const n = values.length;
  const slot = n > 1 ? (W - PAD_L - PAD) / (n - 1) : W - PAD_L - PAD;
  const barW = Math.max(4, Math.min(18, slot * 0.5));
  const gridVals = percent ? [0, 50, 100] : [0, g.domain[1]];
  const last = values.reduce((acc, v, i) => (v === null ? acc : i), -1);
  const hp = hover === null ? null : points[hover];
  const hv = hover === null ? null : values[hover];
  return (
    <figure className={css.chart} aria-label={title}>
      <figcaption className={css.chartTitle}>{title}</figcaption>
      <svg className={css.svg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title}>
        {gridVals.map((v) => (
          <g key={v}>
            <line className={css.grid} x1={PAD_L} x2={W - PAD} y1={g.y(v)} y2={g.y(v)} />
            <text className={css.gridLabel} x={PAD_L - 4} y={g.y(v) + 3} textAnchor="end">{percent ? `${v}%` : format(v)}</text>
          </g>
        ))}
        {kind === "line" && <path className={css.line} d={g.path} />}
        {g.points.map((p, i) => {
          const v = values[i];
          const label = `${pointLabel(points[i], locale)}: ${v === null ? "—" : format(v)}`;
          return (
            <g key={points[i].runId}>
              <rect className={css.hit} x={g.x(i) - slot / 2} y={0} width={slot} height={H}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0}>
                <title>{label}</title>
              </rect>
              {p && kind === "bar" && (
                <rect className={css.bar} x={p.x - barW / 2} y={p.y} width={barW}
                  height={Math.max(0, g.y(0) - p.y)} rx={2} />
              )}
              {p && kind === "line" && (
                <circle className={i === last ? css.markerEnd : css.marker} cx={p.x} cy={p.y} r={i === last ? 5 : 4} />
              )}
            </g>
          );
        })}
        {points.length <= 8 && points.map((p, i) => (
          <text key={p.runId} className={css.axisLabel} x={g.x(i)} y={H - 1} textAnchor="middle">
            {axisLabel(points, i, locale)}
          </text>
        ))}
      </svg>
      <div className={css.tooltip} aria-live="polite">
        {hp && (
          <>
            <span className={css.tooltipStrong}>{pointLabel(hp, locale)}</span>
            {" · "}{hv === null ? "—" : format(hv)}
            {hp.judgement && <> · {t("inventory.trend.wmsOf", { date: shortDate(hp.judgement.asOf, locale) })}</>}
          </>
        )}
      </div>
    </figure>
  );
}

function StatusChip({ status, t }: { status: ChronicStatus; t: T }) {
  const dot = status === "missing_in_ic3" ? css.dotMissing : status === "unexpected_in_ic3" ? css.dotUnexpected : "";
  return (
    <span className={css.status}>
      <span className={`${css.dot} ${dot}`} aria-hidden="true" />
      {t(`inventory.trend.status.${status}`)}
    </span>
  );
}
